import React, { useState } from "react";
import axios from "axios";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";

const API_URL =
  process.env.REACT_APP_APIURL ||
  (process.env.REACT_APP_BASEURL
    ? process.env.REACT_APP_BASEURL.replace("/api/settings/", "/api/")
    : "");

const AccountDeletionPublicPage = () => {
  const [form, setForm] = useState({
    account_type: "",
    full_name: "",
    email: "",
    phone: "",
    reason: "",
    additional_details: "",
  });
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("form"); // form | otp | done
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!form.account_type || !form.full_name || !form.email || !form.reason) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    try {
      setSubmitting(true);
      const baseUrl = API_URL.endsWith("/") ? API_URL : `${API_URL}/`;

      const payload = {
        postData: {
          ...form,
          app_name: "vSARA",
        },
      };

      const response = await axios.post(
        `${baseUrl}AccountDeletionModule/StartAccountDeletion`,
        payload
      );

      if (response.data && response.data.status) {
        setSuccessMessage(
          "We have emailed a one-time verification code to your email address. Please enter it below to confirm your deletion request."
        );
        setStep("otp");
      } else if (response.data && response.data.errors) {
        setErrorMessage(
          response.data.errors[0]?.message ||
            "Unable to start deletion request. Please try again."
        );
      } else {
        setErrorMessage(
          response.data.message ||
            "Unable to start deletion request. Please try again."
        );
      }
    } catch (error) {
      console.error("Account deletion request error:", error);
      setErrorMessage("Server error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!otp || !form.email) {
      setErrorMessage("Please enter the verification code sent to your email.");
      return;
    }

    try {
      setSubmitting(true);
      const baseUrl = API_URL.endsWith("/") ? API_URL : `${API_URL}/`;

      const payload = {
        postData: {
          email: form.email,
          otp: otp,
        },
      };

      const response = await axios.post(
        `${baseUrl}AccountDeletionModule/VerifyAccountDeletionOtp`,
        payload
      );

      if (response.data && response.data.status) {
        setSuccessMessage(
          "Your account deletion request has been submitted successfully. Our team will review it and process the deletion as required."
        );
        setStep("done");
        setOtp("");
        setForm({
          account_type: "",
          full_name: "",
          email: "",
          phone: "",
          reason: "",
          additional_details: "",
        });
      } else {
        setErrorMessage(
          response.data.message ||
            "Unable to verify the code. Please check the code and try again."
        );
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      setErrorMessage("Server error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-main__outer">
      <LoadingOverlay active={submitting} spinner={<DNALoader />}>
        <div
          className="container"
          style={{
            maxWidth: "720px",
            margin: "0 auto",
            padding: "40px 20px 60px",
          }}
        >
          <header style={{ marginBottom: "32px" }}>
            <h1
              style={{
                fontSize: "36px",
                fontWeight: 700,
                marginBottom: "8px",
                color: "#111827",
              }}
            >
              Request Account Deletion
            </h1>
            <p style={{ color: "#4b5563", fontSize: "15px" }}>
              Use this form to request permanent deletion of your{" "}
              <strong>vSARA Doctor</strong>, <strong>vSARA Patient</strong>, or{" "}
              <strong>vSARA Pharmacy</strong>{" "}
            </p>
          </header>

          <section
            style={{
              marginBottom: "24px",
              padding: "16px 18px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              fontSize: "13px",
              color: "#374151",
            }}
          >
            <ul style={{ paddingLeft: "18px", marginBottom: 0 }}>
              <li>
                After verification, your account will be{" "}
                <strong>permanently disabled</strong> and your personal data
                will be removed or anonymized, except where we must keep data
                for legal, fraud prevention, or regulatory reasons (as described
                in our Privacy Policy).
              </li>
              <li>
                If possible, submit this request using the{" "}
                <strong>same email</strong> used in your vSARA account to help
                us verify ownership.
              </li>
            </ul>
          </section>

          {successMessage && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 16px",
                borderRadius: "8px",
                background: "#ecfdf3",
                color: "#166534",
                border: "1px solid #bbf7d0",
                fontSize: "14px",
              }}
            >
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 16px",
                borderRadius: "8px",
                background: "#fef2f2",
                color: "#b91c1c",
                border: "1px solid #fecaca",
                fontSize: "14px",
              }}
            >
              {errorMessage}
            </div>
          )}

          {step === "form" && (
            <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <label
                  htmlFor="account_type"
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  I am a<span style={{ color: "#dc2626" }}> *</span>
                </label>
                <select
                  id="account_type"
                  name="account_type"
                  value={form.account_type}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select account type</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Patient">Patient</option>
                  <option value="Pharmacy">Pharmacy</option>
                </select>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <label
                  htmlFor="full_name"
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Full name<span style={{ color: "#dc2626" }}> *</span>
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={form.full_name}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Email used in app<span style={{ color: "#dc2626" }}> *</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                marginBottom: "16px",
              }}
            >
              <label
                htmlFor="phone"
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Phone number
              </label>
              <input
                id="phone"
                name="phone"
                type="text"
                value={form.phone}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  fontSize: "14px",
                }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label
                htmlFor="reason"
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Reason for deletion<span style={{ color: "#dc2626" }}> *</span>
              </label>
              <textarea
                id="reason"
                name="reason"
                rows="3"
                value={form.reason}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  fontSize: "14px",
                  resize: "vertical",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                htmlFor="additional_details"
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Additional details (optional)
              </label>
              <textarea
                id="additional_details"
                name="additional_details"
                rows="3"
                value={form.additional_details}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  fontSize: "14px",
                  resize: "vertical",
                }}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "10px 22px",
                borderRadius: "9999px",
                border: "none",
                background: submitting ? "#9ca3af" : "#f5a623",
                color: "#fff",
                fontWeight: 600,
                fontSize: "15px",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Submitting..." : "Submit deletion request"}
            </button>
          </form>
          )}

          {step === "otp" && (
            <form onSubmit={handleVerifyOtp}>
              <div style={{ marginBottom: "16px", marginTop: "8px" }}>
                <label
                  htmlFor="otp"
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Verification code
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "16px",
                    letterSpacing: "4px",
                  }}
                />
                <p
                  style={{
                    marginTop: "8px",
                    fontSize: "13px",
                    color: "#6b7280",
                  }}
                >
                  Enter the 6-digit code we sent to <strong>{form.email}</strong>.
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "10px 22px",
                  borderRadius: "9999px",
                  border: "none",
                  background: submitting ? "#9ca3af" : "#f5a623",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "15px",
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                {submitting ? "Verifying..." : "Verify code & submit request"}
              </button>
            </form>
          )}
        </div>
      </LoadingOverlay>
    </div>
  );
};

export default AccountDeletionPublicPage;

